import React, {useState, useEffect} from 'react';
import {Link as RouterLink, withRouter} from 'react-router-dom';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import {makeStyles} from '@material-ui/styles';
import {
    Grid,
    Button,
    IconButton,
    TextField,
    Link,
    FormHelperText,
    Typography
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {signUp, validateID} from '../../apis/api';
import formSchema from '../../common/formSchema';

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100%',
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#3f51b5',
            },
        },
    },
    grid: {
        height: '100%',
    },
    quoteContainer: {
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
    quote: {
        backgroundColor: theme.palette.neutral,
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: 'url(/images/auth.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
    },
    quoteInner: {
        textAlign: 'center',
        flexBasis: '600px',
    },
    quoteText: {
        color: theme.palette.white,
        fontWeight: 300,
    },
    name: {
        marginTop: theme.spacing(3),
        color: theme.palette.white,
    },
    bio: {
        color: theme.palette.white,
    },
    contentContainer: {},
    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    contentHeader: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: theme.spacing(5),
        paddingBototm: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    logoImage: {
        marginLeft: theme.spacing(4),
    },
    contentBody: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
        },
    },
    form: {
        paddingLeft: 100,
        paddingRight: 100,
        paddingBottom: 125,
        flexBasis: 700,
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
        },
    },
    title: {
        marginTop: theme.spacing(3),
    },
    textField: {
        marginTop: theme.spacing(2),
    },
    signUpButton: {
        margin: theme.spacing(2, 0),
    },
    confirmIdButton: {
        margin: theme.spacing(2, 0, 0, 1),
    },
    validateIdErrMsg: {
        marginTop: theme.spacing(1.5),
        color: theme.palette.error.main,
        fontSize: '0.8em',
    },
    warnIcon: {
        fontSize: '1em',
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(2),
        color: theme.palette.error.main,
    },
    confirm: {
        marginTop: theme.spacing(2)
    },
}));

const SignUp = props => {
    const {history} = props;

    const classes = useStyles();

    const [formState, setFormState] = useState({
        isValid: false,
        values: {},
        touched: {},
        errors: {}
    });

    useEffect(() => {
        const errors = validate(formState.values, formSchema);

        setFormState(formState => ({
            ...formState,
            isValid: !errors,
            errors: errors || {}
        }));
    }, [formState.values]);

    const handleChange = event => {
        event.persist();

        setFormState(formState => ({
            ...formState,
            confirmId: (event.target.name === 'id') ? '' : formState.confirmId,
            values: {
                ...formState.values,
                [event.target.name]:
                    event.target.type === 'checkbox'
                        ? event.target.checked
                        : event.target.value
            },
            touched: {
                ...formState.touched,
                [event.target.name]: event.target.value !== ''
            }
        }));
    };

    const handleBack = () => {
        history.goBack();
    };

    const handleValidateID = event => {
        event.preventDefault();

        validateID(formState.values)
            .then((res) => {
                if (!res.isExist) {
                    setFormState(formState => ({
                        ...formState,
                        confirmId: 'success'
                    }));
                    return;
                }
                setFormState(formState => ({
                    ...formState,
                    confirmId: 'error',
                    errors: {
                        ...formState.errors,
                        id: true
                    }
                }));
            }).catch(() => {
                setFormState(formState => ({
                    ...formState,
                    confirmId: 'error',
                    errors: {
                        ...formState.errors,
                        id: true
                    }
                }));
            });
    };

    const errorHandler = () => {
        // error handler
    };

    const handleSignUp = event => {
        event.preventDefault();

        if (formState.confirmId !== 'success') {
            setFormState(formState => ({
                ...formState,
                confirmId: 'error',
                errors: {id: true}
            }));

            return;
        }
        signUp(formState.values)
            .then(res => {
                if (res.signin) {
                    history.push('/sign-in');
                    return;
                }
                errorHandler();
            }).catch(() => {
                errorHandler();
            });
    };

    const hasError = field => !!(formState.touched[field] && formState.errors[field]);

    return (
        <div className={classes.root}>
            <Grid
                className={classes.grid}
                container
            >
                <Grid
                    className={classes.quoteContainer}
                    item
                    lg={5}
                >
                    <div className={classes.quote}>
                        <div className={classes.quoteInner}>
                            <Typography
                                className={classes.quoteText}
                                variant="h1"
                            >
                                Once you say you're going to settle for second, that's what happens to you in life, I
                                find.
                            </Typography>
                            <div className={classes.person}>
                                <Typography
                                    className={classes.name}
                                    variant="body1"
                                >
                                    John F. Kennedy
                                </Typography>
                            </div>
                        </div>
                    </div>
                </Grid>
                <Grid
                    className={classes.content}
                    item
                    lg={7}
                    xs={12}
                >
                    <div className={classes.content}>
                        <div className={classes.contentHeader}>
                            <IconButton onClick={handleBack}>
                                <ArrowBackIcon/>
                            </IconButton>
                        </div>
                        <div className={classes.contentBody}>
                            <form
                                className={classes.form}
                                onSubmit={handleSignUp}
                            >
                                <Typography
                                    className={classes.title}
                                    variant="h2"
                                >
                                    Create new account
                                </Typography>
                                <Typography
                                    color="textSecondary"
                                    gutterBottom
                                >
                                    Use your email to create new account
                                </Typography>
                                <Grid container direction="row" justify="space-between" alignItems="center">
                                    <Grid
                                        item
                                        md={9}
                                        xs={12}
                                    >
                                        <TextField
                                            className={classes.textField}
                                            error={hasError('account_id')}
                                            fullWidth
                                            helperText={
                                                hasError('account_id') ? formState.errors.account_id[0] : null
                                            }
                                            label="ID"
                                            name="account_id"
                                            onChange={handleChange}
                                            type="text"
                                            value={formState.values.account_id || ''}
                                            variant="outlined"
                                            InputLabelProps={{style: {color: 'gray'}}}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                    >
                                        <Button
                                            className={classes.confirm}
                                            color="primary"
                                            type="button"
                                            variant="contained"
                                            onClick={handleValidateID}
                                        >
                                            중복 확인
                                        </Button>
                                    </Grid>
                                </Grid>
                                {formState.confirmId === 'success' && (
                                    <FormHelperText>
                                        유효한 ID 입니다.
                                    </FormHelperText>
                                )}
                                {formState.confirmId === 'error' && (
                                    <FormHelperText error>
                                        이미 존재하거나 유효하지 않은 ID 입니다. 다시 입력해주세요.
                                    </FormHelperText>
                                )}
                                <Grid container direction="row" spacing={2}>
                                    <Grid
                                        item
                                        lg={6}
                                        md={6}
                                        xs={12}
                                    >
                                        <TextField
                                            className={classes.textField}
                                            error={hasError('password')}
                                            fullWidth
                                            helperText={
                                                hasError('password') ? formState.errors.password[0] : null
                                            }
                                            label="Password"
                                            name="password"
                                            onChange={handleChange}
                                            type="password"
                                            value={formState.values.password || ''}
                                            variant="outlined"
                                            InputLabelProps={{style: {color: 'gray'}}}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        lg={6}
                                        md={6}
                                        xs={12}
                                    >
                                        <TextField
                                            className={classes.textField}
                                            error={hasError('confirmPassword')}
                                            fullWidth
                                            helperText={
                                                hasError('confirmPassword') ? formState.errors.confirmPassword[0] : null
                                            }
                                            label="Confirm Password"
                                            name="confirmPassword"
                                            onChange={handleChange}
                                            type="password"
                                            value={formState.values.confirmPassword || ''}
                                            variant="outlined"
                                            InputLabelProps={{style: {color: 'gray'}}}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <FormHelperText>
                                            6~14자 영문 대 소문자, 숫자, 특수문자를 사용하세요.
                                        </FormHelperText>
                                    </Grid>
                                </Grid>
                                <TextField
                                    className={classes.textField}
                                    error={hasError('company_name')}
                                    fullWidth
                                    helperText={
                                        hasError('company_name') ? formState.errors.company_name[0] : null
                                    }
                                    label="회사명"
                                    name="company_name"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.company_name || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('company_business_number')}
                                    fullWidth
                                    helperText={
                                        hasError('company_business_number') ? formState.errors.company_business_number[0] : null
                                    }
                                    label="사업자등록번호"
                                    name="company_business_number"
                                    onChange={handleChange}
                                    type="number"
                                    value={formState.values.company_business_number || ''}
                                    variant="outlined"
                                    placeholder="숫자만 입력하세요."
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('company_owner_name')}
                                    fullWidth
                                    helperText={
                                        hasError('company_owner_name') ? formState.errors.company_owner_name[0] : null
                                    }
                                    label="대표자명"
                                    name="company_owner_name"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.company_owner_name || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('company_address')}
                                    fullWidth
                                    helperText={
                                        hasError('company_address') ? formState.errors.company_address[0] : null
                                    }
                                    label="회사주소"
                                    name="company_address"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.company_address || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('name')}
                                    fullWidth
                                    helperText={
                                        hasError('name') ? formState.errors.name[0] : null
                                    }
                                    label="담당자성명"
                                    name="name"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.name || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('rank')}
                                    fullWidth
                                    helperText={
                                        hasError('rank') ? formState.errors.rank[0] : null
                                    }
                                    label="직급"
                                    name="rank"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.rank || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('dept')}
                                    fullWidth
                                    helperText={
                                        hasError('dept') ? formState.errors.dept[0] : null
                                    }
                                    label="부서"
                                    name="dept"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.dept || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('email')}
                                    fullWidth
                                    helperText={
                                        hasError('email') ? formState.errors.email[0] : null
                                    }
                                    label="담당자 이메일"
                                    name="email"
                                    onChange={handleChange}
                                    type="text"
                                    value={formState.values.email || ''}
                                    variant="outlined"
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('phone_number')}
                                    fullWidth
                                    helperText={
                                        hasError('phone_number') ? formState.errors.phone_number[0] : null
                                    }
                                    label="담당자 전화번호"
                                    name="phone_number"
                                    onChange={handleChange}
                                    type="number"
                                    value={formState.values.phone_number || ''}
                                    variant="outlined"
                                    placeholder="숫자만 입력하세요."
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />
                                <TextField
                                    className={classes.textField}
                                    error={hasError('fax_number')}
                                    fullWidth
                                    helperText={
                                        hasError('fax_number') ? formState.errors.fax_number[0] : null
                                    }
                                    label="팩스번호"
                                    name="fax_number"
                                    onChange={handleChange}
                                    type="number"
                                    value={formState.values.fax_number || ''}
                                    variant="outlined"
                                    placeholder="숫자만 입력하세요."
                                    InputLabelProps={{style: {color: 'gray'}}}
                                />

                                <Button
                                    className={classes.signUpButton}
                                    color="primary"
                                    disabled={!formState.isValid}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    Sign up now
                                </Button>
                                <Typography
                                    color="textSecondary"
                                    variant="body1"
                                >
                                    Have an account?{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/sign-in"
                                        variant="h6"
                                    >
                                        Sign in
                                    </Link>
                                </Typography>
                            </form>
                        </div>
                    </div>
                </Grid>
            </Grid>
        </div>
    );
};

SignUp.propTypes = {
    history: PropTypes.object
};

export default withRouter(SignUp);
